import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {OrderingPhase, SessionModel} from "../../session/session.model";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonConnotation, ButtonProminence} from "../../buttons/button/button";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {OrderModel} from "../order.model";
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {CustomerDetails, CustomerDetailsService} from '../../customer-info/customer-details.service';
import {OrderPlacementStatusData} from "../order-placement-status.service";
import {RecentOrderData} from "../recent-order.service";
import {OrderPlacementStatus} from "../order-placement.service";
import {StoreData} from '../../store/store-search.model';
import {OrderType} from '../order';
import {TranslateModule} from '@ngx-translate/core';
import {FullPageLayoutComponent} from '../../page-layout/full-page-layout/full-page-layout.component';
import {PageHeaderImageComponent} from '../../page-layout/page-header-image/page-header-image.component';
import {ChewzieAttributionComponent} from "../../ui-elements/chewzie-attribution/chewzie-attribution.component";
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {LogoComponent} from "../../ui-elements/logo/logo.component";
import {SpinnerComponent} from "../../ui-elements/spinner/spinner.component";
import {OrderDetailsPanelComponent} from "../order-details-panel/order-details-panel.component";
import {AnalyticsService, OrderPlacedEvent} from '../../logging/analytics.service';
import {HtmlPipe} from '../../utils/html.pipe';
import {PossessivePipe} from '../../utils/possessive.pipe';
import {ElementWidthDirective} from '../../utils/element-width.directive';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {IdentityModel} from '../../identity/identity.model';
import {PromoCarouselComponent} from '../../promo/promo-carousel/promo-carousel.component';

@Component({
    selector: "woa-order-confirmation-page",
    templateUrl: "./order-confirmation-page.component.html",
    styleUrl: "./order-confirmation-page.component.scss",
    providers: [{provide: Page, useExisting: OrderConfirmationPageComponent}],
    imports: [
        ButtonComponent,
        ChewzieAttributionComponent,
        ContentMessageComponent,
        ElementWidthDirective,
        FullPageLayoutComponent,
        HtmlPipe,
        IconComponent,
        LogoComponent,
        OrderDetailsPanelComponent,
        PageFooterControlDirective,
        PageHeaderImageComponent,
        PossessivePipe,
        ResponsiveDirective,
        SpinnerComponent,
        TranslateModule,
        PromoCarouselComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class OrderConfirmationPageComponent extends Page implements OnInit, OnDestroy {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public sessionModel: SessionModel = inject(SessionModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);
    private identityModel: IdentityModel = inject(IdentityModel);

    OrderType = OrderType;
    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    OrderPlacementStatus = OrderPlacementStatus;
    MessageLevel = MessageLevel;

    private static readonly ORDER_ACCEPTANCE_POLL_INTERVAL: number = 5000;

    private readonly orderPlacedEvent: Signal<OrderPlacedEvent> = navParam.required<OrderPlacedEvent>("orderPlacedEvent");

    public readonly progressSize: number = 120;
    public readonly progressStrokeWidth: number = 20;
    public readonly progressRadius: number = (this.progressSize / 2) - (this.progressStrokeWidth - 2);
    public readonly progressCircumference: number = this.progressRadius * 2 * Math.PI;

    public readonly progressOffset: WritableSignal<number> = signal<number>(this.progressCircumference);
    public readonly pendingPhase: WritableSignal<number> = signal<number>(1);
    public readonly recentOrder: WritableSignal<RecentOrderData> = signal(this.orderModel.buildRecentOrderData());

    public readonly hideBackButton: Signal<boolean> = computed(() =>
        this.recentOrder().placedOrder.status === OrderPlacementStatus.PENDING || this.recentOrder().placedOrder.status === OrderPlacementStatus.ACCEPTED);

    public readonly firstName: Signal<string | undefined> = computed(() =>
        this.recentOrder().orderInitiation.transferTargetId ? this.recentOrder().orderInitiation.firstName
            : this.recentOrder().orderPlacement ? this.recentOrder().orderPlacement.firstName
                : undefined);

    public readonly headingTitle: Signal<string> = computed<string>(() =>
        this.configurationModel.isWaiterActor() ? (this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? "woa.heading.order.tableOrderPlaced.service.title" : "woa.heading.order.serviceOrderPlaced")
            : this.recentOrder().orderInitiation.transferTargetId ? (this.firstName() ? "woa.heading.order.orderTransferred.withName" : "woa.heading.order.orderTransferred.anonymous")
                : this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().sendTableOrderReady ? (this.firstName() ? "woa.heading.order.tableBistroOrderPlaced.withName.title" : "woa.heading.order.tableBistroOrderPlaced.anonymous.title")
                    : this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? (this.firstName() ? "woa.heading.order.tableOrderPlaced.withName.title" : "woa.heading.order.tableOrderPlaced.anonymous.title")
                        : (this.firstName() ? "woa.message.order.orderPlaced.withName" : "woa.message.order.orderPlaced.anonymous"));

    public readonly headingNotice: Signal<string> = computed<string>(() =>
        this.configurationModel.isConsumerActor() &&
        this.recentOrder().orderInitiation.tableNumber &&
        !this.recentOrder().orderInitiation.transferTargetId &&
        this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().sendTableOrderReady &&
        this.recentOrder().orderPlacement.mobilePhone &&
        this.configurationModel.isValidMobileNumberForSms()(this.recentOrder().orderPlacement.mobilePhone) ? (
            this.firstName() ? "woa.heading.order.tableBistroOrderPlaced.withName.notice" : "woa.heading.order.tableBistroOrderPlaced.anonymous.notice"
        ) : undefined);

    public readonly showCarousel: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.supportsRegistration() && !this.configurationModel.isWaiterActor() && this.identityModel.promoteMemberLogin());

    private progressSeen: boolean = false;
    private localSecondsElapsed: number;
    private timeoutSecondsPhase2: number;
    private lastSecondsElapsed: number;
    private lastTimeSync: number;
    private timer: NodeJS.Timeout;
    private destroyed: boolean;

    async ngOnInit(): Promise<void> {
        return this.awaitOrderAcceptance();
    }

    // noinspection JSUnusedGlobalSymbols
    override onActive(): void {
        this.sessionModel.orderingPhase.set(OrderingPhase.COMPLETE);
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
    }

    public async back(): Promise<void> {
        if (this.recentOrder().placedOrder.status === OrderPlacementStatus.ACCEPTED) {
            void this.sessionModel.goToLandingPage();
        } else if (this.recentOrder().placedOrder.status !== OrderPlacementStatus.PENDING) { // CANCELLED or REJECTED
            // If this happens, it means the back or return buttons were available, which means there was an ordering failure, and we are allowing the user to go back to the ordering page.
            void this.dismiss();
        }
    }

    // noinspection JSUnusedGlobalSymbols
    public getDirections(store: StoreData): void {
        // noinspection SpellCheckingInspection
        window.open("https://maps.apple.com/?daddr=" + store.latitude + "," + store.longitude, "_system", "noopener");
    }

    private async awaitOrderAcceptance(): Promise<void> {
        this.timer = setInterval(() => {
            if (this.recentOrder().placedOrder.status === OrderPlacementStatus.PENDING && this.pendingPhase() == 2 && this.localSecondsElapsed < this.timeoutSecondsPhase2) {
                this.localSecondsElapsed = this.lastSecondsElapsed + ((new Date().getTime() - this.lastTimeSync) / 1000);
                this.updateProgressOffset();
            }
        }, 1000);

        while (this.recentOrder().placedOrder.status === OrderPlacementStatus.PENDING) {
            const currentStatus: OrderPlacementStatus = this.recentOrder().placedOrder.status;
            await new Promise<void>((resolve) => setTimeout(() => resolve(), OrderConfirmationPageComponent.ORDER_ACCEPTANCE_POLL_INTERVAL));
            if (this.destroyed) {
                return;
            }

            // getOrderPlacementStatus() updates recentOrder.placedOrder.status
            const orderPlacementStatus: OrderPlacementStatusData = await this.orderModel.getOrderPlacementStatus(this.orderModel.canPersistOrderDetails());
            // Trigger signal update to reflect status change
            this.recentOrder.set(this.orderModel.buildRecentOrderData());
            this.timeoutSecondsPhase2 = orderPlacementStatus.timeoutSecondsPhase2;
            this.lastSecondsElapsed = Math.min(orderPlacementStatus.secondsElapsed, this.timeoutSecondsPhase2);
            this.lastTimeSync = new Date().getTime();
            if (orderPlacementStatus.status === OrderPlacementStatus.PENDING && this.lastSecondsElapsed >= orderPlacementStatus.timeoutSecondsPhase1) {
                this.pendingPhase.set(2);
                if (this.localSecondsElapsed == undefined || this.lastSecondsElapsed > this.localSecondsElapsed) {
                    this.localSecondsElapsed = this.lastSecondsElapsed;
                }
                this.updateProgressOffset();
            }
            if (orderPlacementStatus.status !== currentStatus) {
                if (orderPlacementStatus.status === OrderPlacementStatus.ACCEPTED) {
                    await this.analyticsService.trackOrderPlaced(this.orderPlacedEvent());
                } else if (orderPlacementStatus.status === OrderPlacementStatus.REJECTED) {
                    await this.analyticsService.trackPlaceOrderFailed("orderRejected");
                } else if (orderPlacementStatus.status === OrderPlacementStatus.CANCELLED) {
                    await this.analyticsService.trackPlaceOrderFailed("orderCancelled");
                }
            }
        }

        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }

        if (this.orderingContextModel.marketingOptIn() && this.configurationModel.isConsumerActor()) {
            void this.promptForMarketingDetails();
        }
    }

    private async promptForMarketingDetails(): Promise<void> {
        let showMobilePhone: boolean = this.configurationModel.app()?.collectMarketingMobilePhone && !this.recentOrder().orderPlacement.mobilePhone && !this.recentOrder().placedOrder.mobilePhoneKnown;
        let showEmail: boolean = this.configurationModel.app()?.collectMarketingEmail && !this.recentOrder().orderPlacement.email && !this.recentOrder().placedOrder.emailKnown;
        let showPostCode: boolean = this.configurationModel.app()?.collectMarketingPostCode && !this.recentOrder().orderPlacement.address.postCode && !this.recentOrder().placedOrder.postCodeKnown;
        if (!showMobilePhone && !showEmail && !showPostCode) {
            return;
        }

        const result: {
            confirmed: boolean,
            mobilePhone?: string,
            email?: string,
            postCode?: string
        } = await this.navigationService.createPage("woa-marketing-entry-page", {
            showMobilePhone: showMobilePhone,
            showEmail: showEmail,
            showPostCode: showPostCode
        });
        if (result && result.confirmed && (result.mobilePhone || result.email || result.postCode)) {
            if (result.mobilePhone || result.email) {
                const customerDetails: CustomerDetails = this.customerDetailsService.loadCustomer();
                if (result.mobilePhone) {
                    customerDetails.mobilePhone = result.mobilePhone;
                }
                if (result.email) {
                    customerDetails.email = result.email;
                }
                this.customerDetailsService.saveCustomer(customerDetails);
            }
            void this.orderModel.performPostscript(result.email, result.mobilePhone, result.postCode, true);
        }
    }

    private updateProgressOffset(): void {
        setTimeout(() => this.progressOffset.set(
                Math.max(this.progressCircumference - this.localSecondsElapsed / this.timeoutSecondsPhase2 * this.progressCircumference, 0)),
            this.progressSeen ? 0 : 500);
        this.progressSeen = true;
    }
}
